import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Build a Box — an interactive box builder.
 *
 * Customers add products from category tabs into a local box, see reward-tier
 * progress, and add the whole box to the cart in one request. The box is held
 * in client-side state until "Add to cart" — no server round-trips while
 * browsing. Unlocked reward-tier discounts reduce the displayed purchase totals;
 * subscriptions are real — when "subscribe" is selected, eligible
 * lines are added on an Appstle selling plan at the plan's real price.
 *
 * @typedef {object} BoxItem
 * @property {number} quantity
 * @property {string} productId
 * @property {string} title
 * @property {string} variantTitle
 * @property {number} price - Unit price in the shop's minor units (cents).
 * @property {string} image
 * @property {string} category
 * @property {string} packageInfo
 * @property {Record<string, number>} sellingPlans - Map of Appstle selling-plan id → per-delivery price in cents for every plan this variant is enrolled in (empty if none).
 */
class BuildABoxComponent extends Component {
  /** @type {Map<string, BoxItem>} */
  #state = new Map();

  /**
   * @type {{
   *   element: Element,
   *   discount: number,
   *   minTotal: number,
   *   minCategories: number[],
   * }[]}
   */
  #tiers = [];

  /** Category labels in display order (from tab buttons), used for per-category counting. */
  /** @type {string[]} */
  #categoryLabels = [];

  /** @type {'onetime' | 'subscribe'} */
  #purchaseType = 'onetime';

  /** Selected Appstle selling-plan id (delivery frequency) from the picker. '' when none. */
  #sellingPlan = '';

  #moneyFormat = '{{amount}}';

  /** @type {Record<string, string>} */
  #copy = {};

  /** @type {IntersectionObserver | null} */
  #panelObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#moneyFormat = (this.dataset.moneyFormat || '{{amount}}').replace(/&amp;/g, '&');
    this.#copy = this.#readCopy();

    this.#categoryLabels = [...this.querySelectorAll('[data-tab]')]
      .map((tab) => tab.textContent?.trim() || '')
      .filter(Boolean);

    this.#tiers = [...this.querySelectorAll('[data-tier]')].map((el) => ({
      element: el,
      discount: parseFloat(el.getAttribute('data-tier-discount') || '0'),
      minTotal: parseInt(el.getAttribute('data-tier-min-total') || '0', 10),
      minCategories: this.#categoryLabels.map((_, i) =>
        parseInt(el.getAttribute(`data-tier-min-${i + 1}`) || '0', 10)
      ),
    }));

    // The default plan lives in the markup (whichever radio is `checked`); read it
    // here so #purchaseType matches on first paint.
    const checkedPurchase = this.querySelector('input[name^="bab-purchase"]:checked');
    if (checkedPurchase) {
      this.#purchaseType = checkedPurchase.value === 'subscribe' ? 'subscribe' : 'onetime';
    }

    // The default delivery frequency lives in the markup (the selected <option> of the
    // plan dropdown); read it so the selected Appstle plan matches on first paint.
    const frequencySelect = this.querySelector('[data-frequency-select]');
    if (frequencySelect) this.#sellingPlan = frequencySelect.value;

    this.#observePanels();
    this.#updatePurchaseModeUI();
    this.#render();

    // Dev/editor aid: flag products not enrolled in the configured subscription plan.
    this.#warnUnenrolledSubscriptions();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#panelObserver?.disconnect();
    this.#panelObserver = null;
  }

  /**
   * Dev/editor-only sanity check: warns when box products aren't enrolled in the
   * configured Appstle selling plan. Such products are silently added one-time even
   * in subscribe mode, so a wrong/stale plan id (or a product missing from the plan)
   * otherwise fails invisibly. Skipped on the live storefront.
   */
  #warnUnenrolledSubscriptions() {
    const isDev =
      window.Shopify?.designMode === true ||
      ['localhost', '127.0.0.1'].includes(location.hostname) ||
      location.hostname.endsWith('.trycloudflare.com');
    if (!isDev) return;

    // The Appstle plan ids the group exposes come from the frequency picker. No
    // picker → no subscription group on the catalog → nothing to check.
    const planIds = new Set(
      [...this.querySelectorAll('[data-frequency-select] option')].map((option) => option.value).filter(Boolean)
    );
    if (planIds.size === 0) return;

    const cards = [...this.querySelectorAll('.bab-card')];
    if (cards.length === 0) return;

    // A card is subscribe-eligible if any variant (or its single add target) is
    // enrolled in one of the group's plans. data-*-selling-plans is a
    // "planId:price|…" map written from the variant's selling_plan_allocations.
    const carriesPlan = (card) =>
      [...card.querySelectorAll('[data-variant-selling-plans], [data-selling-plans]')].some((el) => {
        const map = el.getAttribute('data-variant-selling-plans') ?? el.getAttribute('data-selling-plans') ?? '';
        return map.split('|').some((pair) => planIds.has(pair.split(':')[0]));
      });

    const unenrolled = cards.filter((card) => !carriesPlan(card));
    if (unenrolled.length === 0) return;

    const titles = unenrolled.map(
      (card) =>
        card.querySelector('[data-add]')?.getAttribute('data-product-title') ||
        card.getAttribute('data-product-id') ||
        '(unknown product)'
    );

    if (unenrolled.length === cards.length) {
      console.warn(
        `[build-a-box] No catalog products are enrolled in the Appstle subscription group. ` +
          `Subscribe mode will silently fall back to one-time — check that these products are ` +
          `attached to the subscription plan in Appstle.`
      );
    } else {
      console.warn(
        `[build-a-box] ${unenrolled.length} of ${cards.length} products are not enrolled in the Appstle ` +
          `subscription group; they'll be added one-time even in subscribe mode:`,
        titles
      );
    }
  }

  /** Highlights the tab matching whichever panel is closest to the top of the viewport. */
  #observePanels() {
    const panels = [...this.querySelectorAll('[data-panel]')];
    if (panels.length === 0) return;

    this.#panelObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) this.#setActiveTab(visible.target.getAttribute('data-panel'));
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    for (const panel of panels) this.#panelObserver.observe(panel);
  }

  /** @param {string | null} index */
  #setActiveTab(index) {
    if (!index) return;
    for (const el of this.querySelectorAll('[data-tab]')) {
      const active = el.getAttribute('data-tab') === index;
      el.classList.toggle('is-active', active);
    }
  }

  /* ------------------------------------------------------------------ *
   * Catalog interactions
   * ------------------------------------------------------------------ */

  /** @param {Event & { target: HTMLElement }} event */
  selectTab(event) {
    const tab = event.target.closest('[data-tab]');
    if (!tab) return;
    const index = tab.getAttribute('data-tab');
    const panel = /** @type {HTMLElement | null} */ (this.querySelector(`[data-panel="${index}"]`));
    if (!panel) return;

    this.#setActiveTab(index);
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** @param {Event & { target: HTMLElement }} event */
  openVariants(event) {
    const card = event.target.closest('.bab-card');
    if (card) {
      delete card.dataset.addingVariant;
      card.classList.add('is-choosing');
    }
  }

  /** Opens the existing picker without replacing a size already in the box. */
  addAnotherVariant(event) {
    const card = event.target.closest('.bab-card');
    if (!card) return;
    card.dataset.addingVariant = 'true';
    card.classList.add('is-choosing');
    const options = [...card.querySelectorAll('.bab-card__variant-option')].filter((option) => !option.disabled);
    const next = options.find((option) => !this.#state.has(option.getAttribute('data-variant-id') || '')) || options[0];
    if (next) {
      this.selectVariantOption({ target: next });
      next.focus();
    }
  }

  /**
   * Opens the shared size-guide modal (KiwiSizing chart). The modal markup is
   * rendered once at the section level; every card's "Size guide" trigger opens it.
   * @param {Event} event
   */
  openSizeGuide(event) {
    event?.preventDefault?.();
    const modal = /** @type {HTMLElement | null} */ (this.querySelector('[data-size-guide-modal]'));
    if (modal) modal.style.display = 'block';
  }

  /**
   * Closes the size-guide modal. Bound on the overlay so a click on the backdrop or
   * the close button (×) closes it, while a click inside the content (e.g. the chart)
   * keeps it open. `on:click` rewrites `event.target` to the bound element, so the real
   * click target is read from `composedPath()`.
   * @param {Event} event
   */
  closeSizeGuide(event) {
    const modal = /** @type {HTMLElement | null} */ (this.querySelector('[data-size-guide-modal]'));
    if (!modal) return;
    const clicked = event.composedPath?.()[0];
    if (clicked instanceof Element && clicked.closest('.size-modal-content') && !clicked.closest('.size-modal-close')) {
      return;
    }
    modal.style.display = 'none';
  }

  /**
   * Commits the current variant selection into the box. From qty 0, adds 1 of the
   * selected variant. If a quantity already exists (user re-opened via "Change size"),
   * just closes the overlay so the count isn't bumped.
   *
   * @param {Event & { target: HTMLElement }} event
   */
  addToBox(event) {
    const add = event.target.closest('[data-add]');
    if (!add) return;
    const card = add.closest('.bab-card');
    if (card?.dataset.addingVariant === 'true') {
      delete card.dataset.addingVariant;
      this.#changeQuantity(this.#readCard(add), 1);
      return;
    }
    const quantity = this.#state.get(add.dataset.variantId || '')?.quantity || 0;
    if (quantity === 0) {
      this.#changeQuantity(this.#readCard(add), 1);
    } else {
      const card = add.closest('.bab-card');
      if (card) card.classList.remove('is-choosing');
    }
  }

  /** @param {Event & { target: HTMLElement }} event */
  selectVariantOption(event) {
    const option = /** @type {HTMLButtonElement | null} */ (event.target.closest('.bab-card__variant-option'));
    const card = event.target.closest('.bab-card');
    if (!option || !card || option.disabled) return;

    for (const el of card.querySelectorAll('.bab-card__variant-option')) {
      const selected = el === option;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', String(selected));
    }

    const add = /** @type {HTMLElement | null} */ (card.querySelector('[data-add]'));
    if (!add) return;

    const oldVariantId = add.dataset.variantId || '';
    const newVariantId = option.getAttribute('data-variant-id') || '';

    add.dataset.variantId = newVariantId;
    add.dataset.variantPrice = option.getAttribute('data-variant-price') || '0';
    add.dataset.variantTitle = option.getAttribute('data-variant-title') || '';
    add.dataset.sellingPlans = option.getAttribute('data-variant-selling-plans') || '';

    const priceDisplay = card.querySelector('[data-price-display]');
    if (priceDisplay) {
      priceDisplay.textContent = this.#formatMoney(Number(add.dataset.variantPrice));
    }

    // Show the selected variant's helper line (weight + pack), hide the others —
    // mirrors the PDP SizeHelper that updates below the size list on selection.
    for (const helper of card.querySelectorAll('[data-variant-desc-for]')) {
      helper.toggleAttribute('hidden', helper.getAttribute('data-variant-desc-for') !== newVariantId);
    }

    const label = option.querySelector('.bab-card__variant-label')?.textContent?.trim() || '';
    const sublabel = option.querySelector('.bab-card__variant-sublabel')?.textContent?.trim() || '';
    const selectedEl = card.querySelector('[data-selected-variant]');
    if (selectedEl) {
      selectedEl.textContent = sublabel ? `${label}: ${sublabel}` : label;
    }

    // If the previous variant was in the box, move its quantity to the new variant.
    if (card.dataset.addingVariant !== 'true' && oldVariantId && oldVariantId !== newVariantId) {
      const existing = this.#state.get(oldVariantId);
      if (existing) {
        const combinedQuantity = existing.quantity + (this.#state.get(newVariantId)?.quantity || 0);
        this.#state.delete(oldVariantId);
        this.#state.set(newVariantId, { ...this.#readCard(add).item, quantity: combinedQuantity });
        this.#render();
      }
    }

    this.#syncAllCards();
  }

  /** @param {Event & { target: HTMLElement }} event */
  increment(event) {
    const add = event.target.closest('[data-add]');
    if (add) this.#changeQuantity(this.#readCard(add), 1);
  }

  /** @param {Event & { target: HTMLElement }} event */
  decrement(event) {
    const add = event.target.closest('[data-add]');
    if (add) this.#changeQuantity(this.#readCard(add), -1);
  }

  /* ------------------------------------------------------------------ *
   * Box (summary) interactions
   * ------------------------------------------------------------------ */

  /** @param {Event & { target: HTMLElement }} event */
  itemIncrement(event) {
    const id = event.target.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    if (id) this.#adjustExisting(id, 1);
  }

  /** @param {Event & { target: HTMLElement }} event */
  itemDecrement(event) {
    const id = event.target.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    if (id) this.#adjustExisting(id, -1);
  }

  /** @param {Event & { target: HTMLElement }} event */
  itemRemove(event) {
    const id = event.target.closest('[data-variant-id]')?.getAttribute('data-variant-id');
    if (!id) return;
    this.#state.delete(id);
    this.#syncAllCards();
    this.#render();
  }

  /** @param {Event & { target: HTMLInputElement }} event */
  setPurchaseType(event) {
    this.#purchaseType = event.target.value === 'subscribe' ? 'subscribe' : 'onetime';
    this.#updatePurchaseModeUI();
    this.#render();
  }

  /** @param {Event & { target: HTMLInputElement }} event */
  setFrequency(event) {
    this.#sellingPlan = event.target.value;
    // Picking a delivery frequency implies subscribing — reflect that in the
    // purchase-type radios so the chosen Appstle plan is what gets added.
    this.#purchaseType = 'subscribe';
    const subscribeRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name^="bab-purchase"][value="subscribe"]')
    );
    if (subscribeRadio && !subscribeRadio.checked) subscribeRadio.checked = true;
    this.#updatePurchaseModeUI();
    this.#render();
  }

  /** Toggles catalog UI that only applies while subscribe mode is selected. */
  #updatePurchaseModeUI() {
    this.classList.toggle('is-subscribe-mode', this.#purchaseType === 'subscribe');
  }

  toggleBox() {
    const open = this.classList.toggle('is-box-open');
    this.querySelector('.build-a-box__box-toggle')?.setAttribute('aria-expanded', String(open));
  }

  /** @param {Event & { target: HTMLElement }} event */
  toggleDisclosure(event) {
    const button = event.target.closest('[aria-controls]');
    if (!button) return;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
  }

  /* ------------------------------------------------------------------ *
   * Add to cart
   * ------------------------------------------------------------------ */

  async addToCart() {
    if (this.#state.size === 0) return;

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add-to-cart]'));
    const error = this.querySelector('[data-error]');
    if (error) error.setAttribute('hidden', '');

    // When "subscribe" is selected, attach the customer's chosen Appstle plan (the
    // selected delivery frequency) to each line enrolled in it, so the line is added
    // as a subscription. Products not enrolled in that plan (or one-time mode) are
    // added as normal one-time items.
    const subscribe = this.#purchaseType === 'subscribe';
    const items = [...this.#state.entries()].map(([id, item]) => {
      // Tag every line so the cart/order can identify products added through Build a Box
      // (for fulfillment, reporting, or bundle-aware cart logic). The leading underscore
      // keeps the property hidden from customer-facing cart/checkout displays.
      /** @type {{ id: number, quantity: number, selling_plan?: number, properties: Record<string, string> }} */
      const line = { id: Number(id), quantity: item.quantity, properties: { '_bundle-item': 'true' } };
      if (subscribe && this.#itemSubscribable(item)) {
        line.selling_plan = Number(this.#sellingPlan);
      }
      return line;
    });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);

    if (button) button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || this.#copyText('cartErrorMessage', 'Could not add to cart'));
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'build-a-box-component',
          sections: data.sections,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        })
      );

      // Open the cart drawer so the customer sees the box they just added. The
      // CartAddEvent above refreshes the drawer's contents; this opens it
      // explicitly (independent of the theme's "auto-open cart drawer" setting).
      // showDialog() no-ops if it's already open, so this is safe alongside auto-open.
      /** @type {(Element & { open?: () => void }) | null} */
      const cartDrawer = document.querySelector('cart-drawer-component');
      cartDrawer?.open?.();

      // Reset the builder once the box has been added.
      this.#state.clear();
      this.#syncAllCards();
      this.#render();
      this.classList.remove('is-box-open');
    } catch (err) {
      console.error('[build-a-box]', err);
      if (error) {
        error.textContent = err instanceof Error ? err.message : String(err);
        error.removeAttribute('hidden');
      }
    } finally {
      if (button) button.disabled = this.#state.size === 0;
    }
  }

  /* ------------------------------------------------------------------ *
   * State helpers
   * ------------------------------------------------------------------ */

  /**
   * Reads box-item data off a card's `[data-add]` element.
   * @param {HTMLElement} add
   * @returns {{ id: string, item: BoxItem }}
   */
  #readCard(add) {
    return {
      id: add.dataset.variantId || '',
      item: {
        quantity: 0,
        productId: add.dataset.productId || '',
        title: add.dataset.productTitle || '',
        variantTitle: add.dataset.variantTitle || '',
        price: Number(add.dataset.variantPrice || 0),
        image: add.dataset.productImage || '',
        category: add.dataset.category || '',
        packageInfo: add.dataset.packageInfo || '',
        sellingPlans: parsePlans(add.dataset.sellingPlans || ''),
      },
    };
  }

  /**
   * Per-delivery price (cents) for `item` under the currently selected plan, or its
   * one-time price when the item isn't enrolled in that plan.
   * @param {BoxItem} item
   */
  #subscribePriceFor(item) {
    if (this.#sellingPlan && item.sellingPlans && this.#sellingPlan in item.sellingPlans) {
      return item.sellingPlans[this.#sellingPlan];
    }
    return item.price;
  }

  /**
   * Whether `item` is enrolled in the currently selected Appstle plan.
   * @param {BoxItem} item
   */
  #itemSubscribable(item) {
    return Boolean(this.#sellingPlan && item.sellingPlans && this.#sellingPlan in item.sellingPlans);
  }

  /**
   * @param {{ id: string, item: BoxItem }} card
   * @param {number} delta
   */
  #changeQuantity(card, delta) {
    if (!card.id) return;
    const existing = this.#state.get(card.id);
    const quantity = (existing?.quantity || 0) + delta;

    if (quantity <= 0) {
      this.#state.delete(card.id);
    } else {
      this.#state.set(card.id, { ...(existing || card.item), quantity });
    }

    this.#syncAllCards();
    this.#render();
  }

  /**
   * @param {string} id
   * @param {number} delta
   */
  #adjustExisting(id, delta) {
    const existing = this.#state.get(id);
    if (!existing) return;
    const quantity = existing.quantity + delta;

    if (quantity <= 0) {
      this.#state.delete(id);
    } else {
      this.#state.set(id, { ...existing, quantity });
    }

    this.#syncAllCards();
    this.#render();
  }

  /** Syncs every catalog card's stepper with the current state. */
  #syncAllCards() {
    for (const add of this.querySelectorAll('[data-add]')) {
      this.#syncCard(/** @type {HTMLElement} */ (add));
    }
  }

  /** @param {HTMLElement} add */
  #syncCard(add) {
    const quantity = this.#state.get(add.dataset.variantId || '')?.quantity || 0;
    const card = add.closest('.bab-card');
    if (card) {
      card.classList.toggle('is-added', quantity > 0);
      if (quantity > 0 && card.dataset.addingVariant !== 'true') card.classList.remove('is-choosing');
      const another = card.querySelector('[data-add-another]');
      if (another) {
        const hasAnySize = [...this.#state.values()].some((item) => item.productId === add.dataset.productId);
        another.toggleAttribute('hidden', !hasAnySize || card.classList.contains('is-choosing'));
      }
    }
    const qty = add.querySelector('[data-qty]');
    if (qty) qty.textContent = String(quantity);
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  #render() {
    const entries = [...this.#state.entries()];
    const totalItems = entries.reduce((sum, [, item]) => sum + item.quantity, 0);
    const subtotal = entries.reduce((sum, [, item]) => sum + item.price * item.quantity, 0);

    const isEmpty = entries.length === 0;

    // The mobile bottom sheet stays collapsed by default (only Next Reward, the
    // item count, and Add to cart show). Close it when the box empties so it
    // can't be left open with nothing to show.
    if (isEmpty) this.classList.remove('is-box-open');

    this.classList.toggle('is-empty', isEmpty);

    // Keep aria-expanded in sync with the is-box-open state (covers auto-open
    // on first add and auto-close on empty, in addition to manual toggle).
    const isOpen = this.classList.contains('is-box-open');
    this.querySelector('.build-a-box__box-toggle')?.setAttribute('aria-expanded', String(isOpen));

    // Items list — grouped by category in the order tabs were defined
    const list = this.querySelector('[data-items-list]');
    if (list) {
      const categoryOrder = this.#categoryLabels;

      /** @type {Map<string, [string, BoxItem][]>} */
      const groups = new Map();
      for (const cat of categoryOrder) groups.set(cat, []);
      for (const entry of entries) {
        const cat = entry[1].category || categoryOrder[0] || 'Items';
        if (!groups.has(cat)) groups.set(cat, []);
        groups.get(cat).push(entry);
      }

      list.innerHTML = [...groups.entries()]
        .filter(([, items]) => items.length > 0)
        .map(
          ([category, items]) => `
          <section class="build-a-box__items-group">
            <h3 class="build-a-box__items-group-heading">${escapeHtml(category)}</h3>
            <ul class="build-a-box__items-sublist" role="list">
              ${items.map(([id, item]) => this.#renderItem(id, item)).join('')}
            </ul>
          </section>`
        )
        .join('');
    }

    const countEl = this.querySelector('[data-items-count]');
    if (countEl) countEl.textContent = String(totalItems);
    for (const el of this.querySelectorAll('[data-mobile-count]')) {
      el.textContent = String(totalItems);
    }

    const counts = this.#computeCounts(entries);
    const tierDiscount = this.#unlockedTierDiscount(counts);

    // One-time shows the plain box total. Subscribe sums each line's real Appstle
    // per-delivery price (the regular price for products without a subscription
    // plan), so the displayed total matches what's charged at checkout. Unlocked
    // reward-tier discounts reduce both totals; the box total is struck through
    // whenever subscribing or a tier discount saves money.
    const subscribeSubtotal = entries.reduce(
      (sum, [, item]) => sum + this.#subscribePriceFor(item) * item.quantity,
      0
    );
    const onetimeFinal = totalItems ? this.#priceAfterTierDiscount(subtotal, tierDiscount) : 0;
    const subscribeFinal = totalItems ? this.#priceAfterTierDiscount(subscribeSubtotal, tierDiscount) : 0;

    this.#updatePriceRow('[data-onetime-original]', '[data-onetime-total]', subtotal, onetimeFinal, totalItems > 0, tierDiscount > 0);
    this.#updatePriceRow('[data-subscribe-original]', '[data-subscribe-total]', subtotal, subscribeFinal, totalItems > 0, subscribeFinal < subtotal);

    // Add to cart button
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add-to-cart]'));
    if (button) button.disabled = totalItems === 0;
    // The button shows what will actually be charged for the selected plan —
    // the discounted per-delivery total while subscribing, the box total otherwise.
    const cartTotal = this.querySelector('[data-cart-total]');
    if (cartTotal) {
      const checkoutTotal = this.#purchaseType === 'subscribe' ? subscribeFinal : onetimeFinal;
      cartTotal.textContent = totalItems ? this.#formatMoney(checkoutTotal) : '';
      cartTotal.toggleAttribute('hidden', totalItems === 0);
    }
    const cartDot = this.querySelector('[data-cart-dot]');
    if (cartDot) cartDot.toggleAttribute('hidden', totalItems === 0);

    this.#renderTiers(counts);
  }

  /**
   * @param {[string, BoxItem][]} entries
   * @returns {{ total: number, byCategory: number[] }}
   */
  #computeCounts(entries) {
    const byCategory = this.#categoryLabels.map((label) =>
      entries
        .filter(([, item]) => item.category === label)
        .reduce((sum, [, item]) => sum + item.quantity, 0)
    );
    const total = entries.reduce((sum, [, item]) => sum + item.quantity, 0);
    return { total, byCategory };
  }

  /**
   * @param {string} id
   * @param {BoxItem} item
   */
  #renderItem(id, item) {
    const hasVariant = item.variantTitle && item.variantTitle !== 'Default Title';
    const meta = [hasVariant ? item.variantTitle : '', item.packageInfo].filter(Boolean).join(' – ');
    return `
      <li class="build-a-box__item" data-variant-id="${escapeAttr(id)}">
        <div class="build-a-box__item-media">
          ${
            item.image
              ? `<img class="build-a-box__item-image" src="${escapeAttr(item.image)}" alt="" width="60" height="60" loading="lazy">`
              : '<span class="build-a-box__item-image"></span>'
          }
          <button
            type="button"
            class="build-a-box__item-remove"
            on:click="/itemRemove"
            data-variant-id="${escapeAttr(id)}"
            aria-label="${escapeAttr(this.#interpolate(this.#copyText('removeItemAriaLabel', 'Remove {{ title }} from box'), { title: item.title }))}"
          >
            <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M5.25 0.75L0.75 5.25M0.75 0.75L5.25 5.25" stroke="currentColor" stroke-width="0.75" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="build-a-box__item-content">
          <p class="build-a-box__item-title">${escapeHtml(item.title)}</p>
          ${meta ? `<p class="build-a-box__item-meta">${escapeHtml(meta)}</p>` : ''}
          <p class="build-a-box__item-price">${this.#formatMoney(item.price)}</p>
        </div>
        <span class="build-a-box__item-qty" aria-label="Quantity ${item.quantity}">${item.quantity}${escapeHtml(this.#copyText('quantitySuffix', 'st'))}</span>
      </li>`;
  }

  /**
   * @param {{ total: number, byCategory: number[] }} counts
   * @param {{ element: Element, discount: number, minTotal: number, minCategories: number[] }} tier
   */
  #tierUnlocked(counts, tier) {
    if (counts.total === 0) return false;
    if (tier.minTotal > 0 && counts.total < tier.minTotal) return false;
    for (let i = 0; i < tier.minCategories.length; i++) {
      const min = tier.minCategories[i];
      if (min > 0 && (counts.byCategory[i] || 0) < min) return false;
    }
    return true;
  }

  /**
   * @param {number} cents
   * @param {number} discountPercent
   */
  #priceAfterTierDiscount(cents, discountPercent) {
    if (!discountPercent || !cents) return cents;
    return Math.round(cents * (1 - discountPercent / 100));
  }

  /** @param {{ total: number, byCategory: number[] }} counts */
  #unlockedTierDiscount(counts) {
    let discount = 0;
    for (const tier of this.#tiers) {
      if (this.#tierUnlocked(counts, tier)) {
        discount = Math.max(discount, tier.discount);
      }
    }
    return discount;
  }

  /**
   * @param {string} originalSelector
   * @param {string} finalSelector
   * @param {number} original - in cents
   * @param {number} final - in cents
   * @param {boolean} hasItems
   * @param {boolean} hasDiscount
   */
  #updatePriceRow(originalSelector, finalSelector, original, final, hasItems, hasDiscount) {
    const finalEl = this.querySelector(finalSelector);
    if (finalEl) finalEl.textContent = this.#formatMoney(final);

    const originalEl = this.querySelector(originalSelector);
    if (originalEl) {
      const show = hasItems && hasDiscount && original !== final;
      originalEl.textContent = show ? this.#formatMoney(original) : '';
      originalEl.toggleAttribute('hidden', !show);
    }
  }

  /** @param {{ total: number, byCategory: number[] }} counts */
  #renderTiers(counts) {
    if (this.#tiers.length === 0) return;

    // Selected pill = the highest-discount tier the box currently qualifies for.
    // When nothing qualifies yet (empty or insufficient box) no pill is selected.
    // This is independent of the "Next Reward" copy (see #nextRewardMessage), which
    // points at the next tier still to unlock. Ties on discount keep the earlier tier.
    const selected = this.#tiers
      .filter((tier) => this.#tierUnlocked(counts, tier))
      .reduce((best, tier) => (best && best.discount >= tier.discount ? best : tier), null);

    // Match by value, not element identity: the tier list is rendered twice
    // (mobile main column + desktop sidebar), so the selected tier must light up
    // its copy in whichever instance is visible — not just the first one in the DOM.
    const isSelected = (tier) =>
      selected != null &&
      tier.discount === selected.discount &&
      tier.minTotal === selected.minTotal &&
      tier.minCategories.every((v, i) => v === selected.minCategories[i]);

    for (const tier of this.#tiers) {
      tier.element.classList.toggle('is-unlocked', this.#tierUnlocked(counts, tier));
      tier.element.classList.toggle('is-current', isSelected(tier));
    }

    const hasUnlockedTier = this.#tiers.some((tier) => this.#tierUnlocked(counts, tier));
    const tierToggleLabel = hasUnlockedTier
      ? this.#copyText('tierListToggleUnlocked', 'See all rewards tiers you just unlocked')
      : this.#copyText('tierListToggle', 'See all reward tiers');
    for (const el of this.querySelectorAll('.build-a-box__tier-toggle-label')) {
      el.textContent = tierToggleLabel;
    }

    // The current unlocked discount (across all tiers, including BAM) drives the
    // mobile "X% Off" tier badge in the box sheet.
    const currentDiscount = this.#unlockedTierDiscount(counts);
    this.#updateTierBadge(currentDiscount);

    // Both the main-column (mobile) and the aside (desktop / open sheet) instances
    // of Next Reward share the same data-* hooks, so the updates fan out to all of them.
    const fills = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-progress-fill]'));
    const labels = this.querySelectorAll('[data-progress-label]');

    const { message, percent } = this.#nextRewardMessage(counts);
    for (const el of fills) el.style.width = `${percent}%`;
    for (const el of labels) el.textContent = message;
  }

  /**
   * Builds the "Next Reward" copy + progress-bar fill.
   *
   * The wording is tailored to the Diapers → Wipes reward ladder on this page and
   * intentionally leaves the BAM category out of the copy (per design):
   *   • 2 diapers            → 5% off
   *   • +1 wipe OR 3 diapers → 10%
   *   • 3 diapers + 1 wipe   → 15% (top reward)
   * A "diaper pack" is one product from the Diapers tab. Counts come from the
   * first two category tabs (1st = Diapers, 2nd = Wipes); if those tabs are
   * relabelled or reordered, update this method to match.
   *
   * @param {{ total: number, byCategory: number[] }} counts
   * @returns {{ message: string, percent: number }}
   */
  #nextRewardMessage(counts) {
    const diapers = counts.byCategory[0] || 0;
    const wipes = counts.byCategory[1] || 0;

    // Highest discount unlocked along the diapers/wipes ladder.
    let tier = 0;
    if (diapers >= 2) tier = 5;
    if (diapers >= 3 || (diapers >= 1 && wipes >= 1)) tier = 10;
    if (diapers >= 3 && wipes >= 1) tier = 15;

    // Progress bar tracks items added toward the top reward (3 diapers + 1 wipe).
    const percent = Math.min(100, ((Math.min(diapers, 3) + Math.min(wipes, 1)) / 4) * 100);

    const packs = (n) =>
      n === 1
        ? this.#copyText('diaperPackSingular', 'diaper pack')
        : this.#copyText('diaperPackPlural', 'diaper packs');

    let message;
    if (tier >= 15) {
      message = this.#copyText('progressMaxUnlocked', '🎉 Max discount unlocked');
    } else if (tier === 10) {
      const needDiapers = 3 - diapers;
      message =
        needDiapers > 0
          ? this.#interpolate(
              this.#copyText('progressAddDiapersFor15', 'Add {{ count }} more {{ packs }} to unlock 15%'),
              { count: String(needDiapers), packs: packs(needDiapers) }
            )
          : this.#copyText('progressAddWipesFor15', 'Add wipes to unlock 15%');
    } else if (tier === 5) {
      // Reachable only at exactly 2 diapers / 0 wipes — two ways to reach 10%.
      message = this.#copyText(
        'progressUnlock10',
        'Add wipes to unlock 10% · or add 1 more diaper for 10%'
      );
    } else if (diapers === 1) {
      message = this.#copyText('progressUnlock5One', 'Add 1 more diaper pack to unlock 5% off');
    } else {
      message = this.#copyText('progressUnlock5Two', 'Add 2 diaper packs to unlock 5% off');
    }

    return { message, percent };
  }

  /**
   * Updates the mobile box-sheet tier badge: grayed out showing the lowest tier's
   * discount until any reward unlocks, then green with the current discount.
   * @param {number} currentDiscount
   */
  #updateTierBadge(currentDiscount) {
    const badge = this.querySelector('[data-sheet-tier]');
    const pct = this.querySelector('[data-sheet-tier-pct]');
    if (!badge || !pct) return;

    const active = currentDiscount > 0;
    const lowest = this.#tiers.reduce((min, t) => Math.min(min, t.discount), Infinity);
    const shown = active ? currentDiscount : (Number.isFinite(lowest) ? lowest : 0);

    // Only write when the value changes so the aria-live badge doesn't re-announce
    // on every box update.
    const text = `${shown}% ${this.#copyText('discountOffSuffix', 'Off')}`;
    if (pct.textContent !== text) pct.textContent = text;
    badge.classList.toggle('is-active', active);
  }

  /** @returns {Record<string, string>} */
  #readCopy() {
    const el = this.querySelector('[data-bab-copy]');
    if (!el?.textContent) return {};
    try {
      const parsed = JSON.parse(el.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {string} key
   * @param {string} fallback
   */
  #copyText(key, fallback) {
    const value = this.#copy[key];
    return typeof value === 'string' && value.trim() !== '' ? value : fallback;
  }

  /**
   * @param {string} template
   * @param {Record<string, string>} vars
   */
  #interpolate(template, vars) {
    return Object.entries(vars).reduce(
      (text, [key, value]) => text.replaceAll(`{{ ${key} }}`, value).replaceAll(`{{${key}}}`, value),
      template
    );
  }

  /* ------------------------------------------------------------------ *
   * Money formatting (minimal Shopify money_format support)
   * ------------------------------------------------------------------ */

  /** @param {number} cents */
  #formatMoney(cents) {
    const number = (cents / 100);

    /**
     * @param {number} decimals
     * @param {string} decimalSep
     * @param {string} thousandsSep
     */
    const build = (decimals, decimalSep, thousandsSep) => {
      const fixed = number.toFixed(decimals);
      let [whole, fraction] = fixed.split('.');
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return fraction ? `${whole}${decimalSep}${fraction}` : whole;
    };

    return this.#moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
      switch (name) {
        case 'amount':
          return build(2, '.', ',');
        case 'amount_no_decimals':
          return build(0, '.', ',');
        case 'amount_with_comma_separator':
          return build(2, ',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return build(0, ',', '.');
        case 'amount_with_space_separator':
          return build(2, ',', ' ');
        case 'amount_no_decimals_with_space_separator':
          return build(0, ',', ' ');
        case 'amount_with_apostrophe_separator':
          return build(2, '.', '’');
        default:
          return build(2, '.', ',');
      }
    });
  }
}

/**
 * Parses a "planId:priceCents|planId:priceCents" map (written by the card snippet from
 * a variant's selling_plan_allocations) into an object keyed by selling-plan id.
 * @param {string} value
 * @returns {Record<string, number>}
 */
function parsePlans(value) {
  /** @type {Record<string, number>} */
  const map = {};
  if (!value) return map;
  for (const pair of value.split('|')) {
    const [id, price] = pair.split(':');
    if (id) map[id] = Number(price);
  }
  return map;
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** @param {string} value */
function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

if (!customElements.get('build-a-box-component')) {
  customElements.define('build-a-box-component', BuildABoxComponent);
}
